// registration component
import React, { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../../app-layouts/AuthLayout";
import {
  RegisterStepOneSchema,
  RegisterStepOneType,
} from "../../app-schemas/auth.schema";
import apiClient from "../../api-services/ApiClient";
import { inputStyle } from "../../app-util/helpers";
import { successAlert } from "../../app-util/SweetAlert";
import { SITELINKS } from "../../app-routes/links";
import { HoneConnectLogo } from "../../assets/img";
import PasswordToggle from "../../app-components/PasswordToggle";
import { GoogleIcon } from "../../assets/custom-icons";
import { LinkedinIcon } from "../../assets/custom-icons";
import { GOOGLE_URL, LINKEDIN_URL } from "../../api-services/process";

const RegisterStepOne = () => {
  // component-state
  const navigate = useNavigate();

  const [pageLoading, setPageLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<
    Partial<RegisterStepOneType>
  >({});

  // use-state-form-errors
  const [errors, setErrors] = useState<
    Partial<Record<keyof RegisterStepOneType, string>>
  >({});

  // server-error-display
  const [serverError, setServerError] = useState<string>("");

  // handle-input-change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegistrationData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // handle-login-submit
  const handleEmailSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    setServerError("");

    const result = RegisterStepOneSchema.safeParse(registrationData);
    if (result.success) {
      try {
        setPageLoading(true);
        const data = { ...result.data };
        await apiClient.post("register-step-one", data);

        successAlert();
        setRegistrationData({});
        navigate(SITELINKS.LOGIN);
      } catch (error: any) {
        console.log(error);
        setServerError(JSON.stringify(error));
      } finally {
        setPageLoading(false);
      }
    } else {
      const formattedErrors: Partial<
        Record<keyof RegisterStepOneType, string>
      > = {};

      result.error.errors.forEach((err) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof RegisterStepOneType;
          formattedErrors[key] = err.message;
        }
      });

      setErrors(formattedErrors);
      console.log(formattedErrors);
    }
  };

  const handleGoogleLogin = () => {
    setPageLoading(true);
    window.location.href = GOOGLE_URL;
  };

  // handle-linkedin-login
  const handleLinkedInLogin = () => {
    setPageLoading(true);
    window.location.href = LINKEDIN_URL;
  };

  return (
    <AuthLayout>
      <div className="lg:col-span-3 bg-white shadow relative h-[100vh]">
        <div className="bg-white w-full">
          <div className="w-1/2 mx-auto pt-12 flex gap-2 mb-10 justify-center">
            <div className="bg-honeConnectPurple h-[5px] w-[40px] shadow rounded-full"></div>
            <div className="bg-gray-300 h-[5px] w-[40px] shadow rounded-full"></div>
            <div className="bg-gray-300 h-[5px] w-[40px] shadow rounded-full"></div>
          </div>

          <div className="text-sm font-semibold flex justify-center px-4">
            <Link to="#!" className="text-honeConnectPurple flex">
              <img
                src={HoneConnectLogo}
                alt="HoneConnect"
                className="h-[35px]"
              />
            </Link>

            <p className="uppercase text-honeConnectGray hidden sm:block">
              Step 1/3
            </p>
          </div>

          {/* <!-- page lead text --> */}
          <div className="px-5 pt-6 pb-5">
            <h4 className="text-[2.5rem] text-honeConnectBlack font-medium mb-2.5 text-honeConnectPurple leading-[35px] text-center">
              Create an <br />
              account
            </h4>
            <p className="text-sm mb-4 font-medium w-4/5 hidden">
              Don't have an account?, it takes less than a minute.
            </p>
          </div>
        </div>

        {/* form section */}
        <div className="relative top-[-10px] w-full pt-4 lg:pt-20 pb-0 px-5 lg:px-10 lg:px-28 text-honeConnectGray bg-white rounded-t-lg">
          {/* social media connect */}
          <div className="flex gap-5">
            <div className="flex flex-col text-center w-1/2">
              <button
                type="button"
                onClick={handleLinkedInLogin}
                className="flex items-center bg-transparent text-gray-500 w-full py-2 px-3 rounded-xl border-1 border border-solid border-gray-200 text-sm  shadow-sm hover:shadow"
              >
                <img
                  src={LinkedinIcon}
                  alt="Google Icon"
                  className="w-[30px] h-[25px]"
                />
                <div className="w-3/4 font-[500] font-medium">
                  <span> LinkedIn</span>
                </div>
              </button>
              <span className="text-[10px] text-honeConnectOrange">
                Recommended
              </span>
            </div>

            <div className="flex flex-col text-center w-1/2">
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="flex items-center bg-transparent text-gray-500 w-full py-2 px-3 rounded-xl border-1 border border-solid border-gray-200 text-sm  shadow-sm hover:shadow"
              >
                <img
                  src={GoogleIcon}
                  alt="Google Icon"
                  className="w-[25px] h-[25px]"
                />
                <div className="w-3/4 font-[500] font-medium">
                  <span> Google</span>
                </div>
              </button>
            </div>
          </div>

          <div className="flex justify-center text-gray-500 text-sm py-5 pb-2">
            <p>— or sign up with —</p>
          </div>

          <form
            onSubmit={handleEmailSignUp}
            autoComplete="off"
            autoCorrect="off"
            className=""
          >
            {/* enter email */}
            <div className="flex flex-col text-sm font-medium mb-3 text-gray-500">
              <label htmlFor="email" className="mb-0.5 text-[12px]">
                Email
              </label>
              <input
                onChange={handleInputChange}
                type="text"
                className={`${inputStyle} bg-transparent border-gray-200 text- rounded-[0px] outline-none focus:outline-none font-medium text-[12px]  shadow-sm hover:shadow`}
                placeholder="Enter email"
                name="email"
                style={{ outline: "none" }}
              />
              <span className="text-red-600 text-[10px]">{errors.email}</span>
            </div>

            {/* enter password */}
            <div className="flex flex-col text-sm text-gray-500 font-medium mb-3 relative">
              <label htmlFor="cPassword" className="mb-0.5 text-[12px]">
                Password
              </label>

              <input
                onChange={handleInputChange}
                type="password"
                className={`${inputStyle} bg-transparent border-gray-200 text- rounded-[0px] outline-none focus:outline-none font-medium text-[12px] shadow-sm hover:shadow`}
                placeholder="Enter your password"
                name="password"
                id="password"
                style={{ outline: "none" }}
              />

              <span className="text-red-600 text-[10px]">
                {errors.password}
              </span>

              <span className="absolute right-3 top-8">
                <PasswordToggle targetId="password" />
              </span>
            </div>

            <p className="text-[#ff9b9b] text-[12px] text-center mb-2">
              {serverError}
            </p>

            <button
              type="submit"
              className="disabled:bg-gray-600 bg-honeConnectPurple text-[#f2f2f2] text-sm w-full p-3 py-4 border-0 mt-2 hover:shadow-sm rounded-lg border-[1px] border-[#8a42b6]  shadow-sm hover:shadow"
              disabled={pageLoading}
            >
              Create account
            </button>

            <div className="text-center mt-8 text-sm text-gray-500">
              Already have an account?{" "}
              <Link
                to={SITELINKS.LOGIN}
                className="text-honeConnectOrange font-semibold underline"
              >
                Sign In
              </Link>
            </div>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};

export default RegisterStepOne;
